import { Locale, format } from 'date-fns';
import { ptBR, ru, zhCN } from 'date-fns/locale';

import { useUserLocale } from './useUserLocale';

/**
 * date-fns' abbreviated weekday is not the short one people write in every
 * locale: ru `EEE` is "пнд" and pt-BR `EEE` is the whole "segunda". The
 * `EEEEEE` width is what both actually use -- "пн", "seg".
 */
export const ShortWeekdayMapping: Record<string, string> = {
  E: 'EEEEEE',
  'EEE, dd MMM': 'EEEEEE, dd MMM',
  'EEE, dd MMM yyy': 'EEEEEE, dd MMM yyy',
};

/**
 * The patterns across the app are written in European day-month order, which
 * Chinese reads as foreign: 'EEEE, dd MMM' renders "星期三, 16 9月" and
 * 'LLLL yyyy' renders "九月 2026". Chinese puts the year first and the weekday
 * last, and marks each part — "9月16日 星期三", "2026年9月" — so every pattern
 * that names a day or a month has its own zh-CN spelling here.
 *
 * 'dd MMM yyyy' is the exception: it fills the date capsules in
 * the event form, and "2026年9月16日" wraps onto two lines there, so it takes
 * the compact numeric form Chinese UIs use in fields, "2026/9/16".
 *
 * A pattern missing from this table still renders, just in European order, so
 * `useLocalisedDateFormat.spec.ts` scans the source for format patterns and
 * fails on any it cannot find here.
 */
export const ZhCNFormatMapping: Record<string, string> = {
  'dd MMM': 'M月d日',
  'dd MMMM': 'M月d日',
  'dd MMMM yyy': 'yyyy年M月d日',
  'dd MMM yyy': 'yyyy年M月d日',
  'dd MMM yyyy': 'yyyy/M/d',
  'd MMM, HH:mm': 'M月d日 HH:mm',
  'd MMMM': 'M月d日',
  'd MMMM yyy': 'yyyy年M月d日',
  'EEE, dd MMM': 'M月d日 EEE',
  'EEE, dd MMM yyy': 'yyyy年M月d日 EEE',
  'EEEE, dd MMM': 'M月d日 EEEE',
  'EEEE, dd MMM · HH:mm': 'M月d日 EEEE · HH:mm',
  'EEEE, dd MMM yyy': 'yyyy年M月d日 EEEE',
  'EEEE, d MMMM yyy': 'yyyy年M月d日 EEEE',
  'LLLL yyyy': 'yyyy年M月',
};

const FormatStringMappings = new Map<Locale, Record<string, string>>([
  [ptBR, ShortWeekdayMapping],
  [ru, ShortWeekdayMapping],
  [zhCN, ZhCNFormatMapping],
]);

export function localiseFormatString(formatStr: string, locale: Locale) {
  return FormatStringMappings.get(locale)?.[formatStr] ?? formatStr;
}

export function useLocalisedDateFormat() {
  const { userLocale } = useUserLocale();
  const localisedFormat: typeof format = (date, formatStr, options) =>
    format(date, localiseFormatString(formatStr, userLocale), {
      ...options,
      locale: userLocale,
    });

  return {
    format: localisedFormat,
    userLocale,
  };
}
